import axiosInstance from './axiosInstance';
import { API_PATH, API_VERSION } from './constants';

// Builds the ?page=&page_size=&search= suffix, omitting anything not supplied so
// that "no paging" stays indistinguishable from the pre-pagination request.
const buildProviderQuery = ({ page, pageSize, search } = {}) => {
    const params = new URLSearchParams();
    if (page !== undefined && page !== null) params.append('page', page);
    if (pageSize !== undefined && pageSize !== null) params.append('page_size', pageSize);
    if (search !== undefined && search !== null && String(search).trim() !== '') {
        params.append('search', String(search).trim());
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

const engagementService = {
    // `opts` carries optional server-side paging/search. Omitting it sends no
    // query string at all, so the request is byte-identical to before and the
    // API returns every provider exactly as it used to.
    // Table paging only: hits the data service's provider-page endpoint, which
    // returns just the rows + total. Used for page turns and search so the
    // dashboard's graphs and counts are NOT refetched every time.
    engagementProviderPage: (companyId, type, startRange, endRange, page=1, pageSize=10, search=null) => axiosInstance.get(
        `/${API_VERSION}/provider/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${type}/${startRange}/${endRange}/${page}/${pageSize}`
        + (search && String(search).trim() !== '' ? `?search=${encodeURIComponent(String(search).trim())}` : '')
    ),

    digitalEngagement: (companyId, startRange, endRange, isUnified=true, opts={}) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.DIGITAL}/${startRange}/${endRange}/${isUnified}`
        + buildProviderQuery(opts)
    ),

    fieldEngagement: (companyId, startRange, endRange, isUnified=true) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.FIELD}/${startRange}/${endRange}/${false}/${isUnified}`
    ),

    fieldExtendedRegion: (companyId, startRange, endRange, isUnified=true) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.REGION_EXTENDED}/${startRange}/${endRange}/${isUnified}`
    ),

    fieldEngagementSegment: (companyId, segment, startRange, endRange, isUnified=true) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.SEGMENT_EXTENDED}/${startRange}/${endRange}/${segment}/${isUnified}`
    ),

    messageEngagement: (companyId, startRange, endRange) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.MESSAGES}/${startRange}/${endRange}`
    ),

    prescriberEngagement: (companyId, startRange, endRange) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.PRESCRIBERS}/${startRange}/${endRange}`
    ),

    engagersEngagement: (companyId, startRange, endRange, size) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.TOP_ENGAGERS}/${startRange}/${endRange}/${size}`
    ),
    allEngagersEngagement: (companyId, startRange, endRange ) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.ALL_ENGAGERS}/${startRange}/${endRange}`
    ),

    summaryEngagement: (companyId, startRange, endRange) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.SUMMARY}/${startRange}/${endRange}`
    ),

    totalEngagement: (companyId, startRange, endRange, isUnified=true, opts={}) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.TOTAL}/${startRange}/${endRange}/${isUnified}`
        + buildProviderQuery(opts)
    ),
    totalEngagementDigitalEngagementChart: (companyId, startRange, endRange) => axiosInstance.get(
        `/${API_VERSION}/${API_PATH.COMPANIES}/${companyId}/${API_PATH.ENGAGEMENT}/${API_PATH.TRIGGERS_FTM}/${startRange}/${endRange}`
    )
};

export default engagementService;
